// Value coercion shared by every surface, admin and watcher alike.
//
// The video record has no schema -- `api/addupdate.js` stores whatever JSON it
// is handed -- so the same field can arrive as a string, a boolean or not at
// all depending on which version of the admin wrote it. Reading is therefore
// permissive and writing is strict: read* accepts every shape found in the
// data, write* emits only the shape the front-end can actually read.

// Home.js and VPlayer.js gate on `video.frontend === 'true'` and
// `video.public === 'true'` -- string comparisons. A record holding a real
// boolean `true` fails both, which is why videos added through the old
// checkbox dialog never appeared on the site.
export function readBool(value) {
  return value === true || String(value).toLowerCase() === 'true';
}

export function writeBool(value) {
  return value ? 'true' : 'false';
}

// A field the site will misread: stored as a boolean where a 'true'/'false'
// string is required. Drives the repair action in the header.
export function isLegacyBool(value) {
  return typeof value === 'boolean';
}

// `chatbot` is an opt-out, not a flag: videoChat.js disables the chat button
// only on the literal 'false', so an absent field means enabled.
export function readOptOut(value) {
  return String(value).toLowerCase() !== 'false';
}

// Timestamps are zero-padded so a column of them aligns: 01:05, not 1:05.
// Hours only appear once they exist.
export function formatTime(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Accepts "1:33", "01:33.5", "1:02:05" or a bare number of seconds. Returns
// null when the text is not a time, so the caller can flag the row instead of
// writing NaN into a chapter.
export function parseTimeInput(text) {
  const trimmed = String(text).trim();
  if (!trimmed) return null;

  const hms = trimmed.match(/^(\d+):([0-5]?\d):([0-5]?\d(?:\.\d+)?)$/);
  if (hms) return Number(hms[1]) * 3600 + Number(hms[2]) * 60 + Number(hms[3]);

  const ms = trimmed.match(/^(\d+):([0-5]?\d(?:\.\d+)?)$/);
  if (ms) return Number(ms[1]) * 60 + Number(ms[2]);

  const bare = Number(trimmed);
  return Number.isFinite(bare) && bare >= 0 ? bare : null;
}

// `products` is stored as a comma-separated string, but Home.js:218 splits it
// into an array in place -- so a record can hold either shape depending on
// which page loaded it first. Both are accepted here.
// Deduplicated, because a products list is a set: owning THE_STRIP twice is
// not a thing, and the freeSolo pickers in the admin will happily let you add
// the same name a second time. Normalizing here means entry, storage, search
// and the reachability check all agree without each remembering to dedupe.
export function parseProducts(value) {
  const list = Array.isArray(value)
    ? value.map((p) => String(p).trim())
    : String(value || '')
        .split(',')
        .map((p) => p.trim());
  return [...new Set(list.filter(Boolean))];
}

export function serializeProducts(list) {
  return parseProducts(list).join(', ');
}

// A plugin can be retired: still owned by everyone who bought it, no longer on
// sale. `getproducts` marks those records `depreciated` -- the store's
// spelling, kept verbatim here and in every name below, because a helper called
// `deprecated` reading a key called `depreciated` is a silent no-op waiting to
// happen. An absent flag retires nothing, so an old cached productInfo degrades
// to the whole catalogue rather than to an empty one.
export function readDepreciatedProducts(productInfo) {
  if (!productInfo || typeof productInfo !== 'object') return new Set();
  return new Set(
    Object.keys(productInfo).filter((name) => readBool(productInfo[name]?.depreciated))
  );
}

// `products` is an OR -- owning any one of them unlocks the video -- so a video
// only counts as retired once every product on it is. One live plugin in the
// list keeps it in the catalogue.
//
// `public` deliberately does not exempt a video here. Public means free to
// watch, not current: a free video for a dead plugin is still a video for a
// dead plugin, and exempting it left the retired product holding a filter pill
// in everyone's row. Retagging it to the successor is what promotes it back.
export function isDepreciatedVideo(video, depreciatedProducts) {
  if (!depreciatedProducts || depreciatedProducts.size === 0) return false;
  const products = parseProducts(video?.products);
  return products.length > 0 && products.every((product) => depreciatedProducts.has(product));
}

// Products carry their version in the name -- THE_SHAPER, THE_SHAPER 2,
// WHATTHEKEY 3 -- and an unnumbered name is the first of its line. Every
// retired product in the store today has a successor named this way, so the
// convention is the whole lookup: there is no successor field to read.
export function splitProductVersion(name) {
  const trimmed = String(name ?? '').trim();
  const numbered = trimmed.match(/^(.*?)\s+(\d+)$/);
  return numbered
    ? { base: numbered[1], version: Number(numbered[2]) }
    : { base: trimmed, version: 1 };
}

// Owning THE_SHAPER *and* THE_SHAPER 2 puts the v1 material behind you. The
// upgrade is what retires those videos for you, not the flag on the store --
// owning only THE_SHAPER leaves them as your manual. Any higher version of the
// same line counts, so skipping a version still supersedes.
export function isSupersededFor(product, ownedProducts) {
  const { base, version } = splitProductVersion(product);
  return parseProducts(ownedProducts).some((owned) => {
    const candidate = splitProductVersion(owned);
    return candidate.base === base && candidate.version > version;
  });
}

// The question a retired video has to answer before it stays in the
// catalogue: does this viewer still run one of the plugins on it?
export function stillDependsOn(products, ownedProducts) {
  const owned = parseProducts(ownedProducts);
  return parseProducts(products).some(
    (product) => owned.includes(product) && !isSupersededFor(product, owned)
  );
}
